import React, { type FC, useEffect, useState } from 'react';
import sx from 'mui-sx';

import {
  LessonGoBackLine,
  LingoLiftLessonHeader,
  MainLayout,
  LingoLiftCollectionLessonFooter,
} from '../../widgets';
import Div from '../../shared/ui/Div';
import {
  useGetLingoLiftStudentCollectionAscLessonDetailsQuery,
  useGetLingoLiftStudentCollectionLessonDetailsQuery,
  usePostLingoLiftStudentCollectionAssignmentCompletionsMutation,
} from '../../entities/lingoLiftCollection';
import LingoLiftCollectionLessonCard from '../../entities/lingoLiftCollection/ui/CollectionLessonCard';
import { type ILingoLiftCollectionLesson } from '../../entities/lingoLiftCollection/model/types';
import { ECSDKPlayer } from '../../shared/components/ECSDKPlayer';

import styles from './styles';

// Define the interface for lesson details response
interface ILingoLiftCollectionLessonDetails {
  item: any; // Type for the lesson item
  queue: ILingoLiftCollectionLesson[];
  nextItem: {
    url: string;
  };
  nextItemLabel: string;
}

interface ILingoLiftCollectionsLessonStudentShowProps {
  id: number;
  accessToken: string;
  partnerKey: string;
  dialogId: number;
  collectionId: number;
  ecAscId?: number;
  languageCode: string;
  seeAllLessonsLabel: string;
}

const LingoLiftCollectionsLessonStudentShowPage: FC<
  ILingoLiftCollectionsLessonStudentShowProps
> = props => {
  const {
    id,
    accessToken,
    partnerKey,
    dialogId,
    collectionId,
    ecAscId,
    languageCode,
    seeAllLessonsLabel,
  } = props;

  const [expand, setExpand] = useState<boolean>(true);

  const { data: lessonDetails, isLoading: isDefaultLessonDetailsLoading } =
    useGetLingoLiftStudentCollectionLessonDetailsQuery(
      {
        collectionId,
        lessonId: id,
      },
      {
        skip: Boolean(ecAscId),
      },
    ) as { data?: ILingoLiftCollectionLessonDetails; isLoading: boolean };

  const { data: lessonAscDetails, isLoading: isAscLessonLoading } =
    useGetLingoLiftStudentCollectionAscLessonDetailsQuery(
      {
        lessonId: id,
        ecAscId: ecAscId!,
      },
      {
        skip: !ecAscId,
      },
    ) as { data?: ILingoLiftCollectionLessonDetails; isLoading: boolean };

  const [onCreateAssignmentCompletions, { data: result }] =
    usePostLingoLiftStudentCollectionAssignmentCompletionsMutation();

  useEffect(() => {
    onCreateAssignmentCompletions({
      llCollectionAssignmentCompletion: { enhancedCollectionId: Number(collectionId) },
    });
  }, []);

  const handleExpand = () => setExpand(!expand);

  const isLoading = isAscLessonLoading || isDefaultLessonDetailsLoading;

  return (
    <MainLayout isLoading={isLoading} backgroundColor="white">
      <LessonGoBackLine title={seeAllLessonsLabel} />
      {(lessonDetails || lessonAscDetails) && (
        <>
          <Div
            sx={sx({
              sx: styles.wrapper,
              condition: expand,
            })}
          >
            {expand && (lessonDetails || lessonAscDetails) && (
              <Div sx={styles.sideBar}>
                {(lessonDetails || lessonAscDetails)?.queue.map(
                  (card: ILingoLiftCollectionLesson, index: number) => (
                    <LingoLiftCollectionLessonCard
                      key={`lesson-card-${index}-${card.index}`}
                      card={card}
                      index={index + 1}
                      isRounded={index === 0}
                    />
                  ),
                )}
              </Div>
            )}
            <Div sx={styles.container}>
              {lessonDetails && <LingoLiftLessonHeader {...lessonDetails.item} setOpenElpModal={() => {}} onToggleFavorite={() => {}} />}
              {lessonAscDetails && <LingoLiftLessonHeader {...lessonAscDetails.item} setOpenElpModal={() => {}} onToggleFavorite={() => {}} />}
              <Div sx={styles.iframe} id="player-container" />
            </Div>
          </Div>
          {lessonDetails && (
            <LingoLiftCollectionLessonFooter
              url={lessonDetails.nextItem.url}
              nextItemLabel={lessonDetails.nextItemLabel}
              lessonName={lessonDetails.item.name}
              allLessons={lessonDetails.queue}
              onExpand={handleExpand}
              assignmentCompletionId={result?.id}
            />
          )}

          {lessonAscDetails && (
            <LingoLiftCollectionLessonFooter
              url={lessonAscDetails.nextItem.url}
              nextItemLabel={lessonAscDetails.nextItemLabel}
              lessonName={lessonAscDetails.item.name}
              allLessons={lessonAscDetails.queue}
              onExpand={handleExpand}
              assignmentCompletionId={result?.id}
            />
          )}
        </>
      )}
      {!!(lessonDetails || lessonAscDetails) && (
        <ECSDKPlayer
          partnerKey={partnerKey}
          accessToken={accessToken}
          dialogId={dialogId}
          languageCode={languageCode}
        />
      )}
    </MainLayout>
  );
};

export default LingoLiftCollectionsLessonStudentShowPage;
